/**
 * @fileoverview Staging External URLs API service and hooks for URL-to-Variant mapping.
 * Replaces the legacy googleLinks.ts with the new generalized URL matching pattern.
 * @module features/sku-alias/api/stagingExternalUrls
 */

import { z } from 'zod'
import {
  useQuery,
  useMutation,
  useQueryClient,
  type UseQueryResult,
  type UseMutationResult,
} from '@tanstack/react-query'
import apiClient from '@/lib/apiClient'

// ============================================================================
// Schemas
// ============================================================================

/**
 * Staging External URL schema matching backend StagingExternalUrlDTO.
 */
export const stagingExternalUrlSchema = z.object({
  uuid: z.string().uuid(),
  rawUrl: z.string(),
  canonicalPath: z.string(),
  source: z.string(),
  variantId: z.number().nullable(),
  variantSku: z.string().nullable(),
  suggestedVariantId: z.number().nullable(),
  suggestedVariantSku: z.string().nullable(),
  suggestedVariantName: z.string().nullable(),
  // Tag assignment fields. `.default(null)` keeps older cached payloads parseable.
  tagUuid: z.string().uuid().nullable().default(null),
  tagSlug: z.string().nullable().default(null),
  tagName: z.string().nullable().default(null),
  resolved: z.boolean(),
  createdAt: z.string(),
  updatedAt: z.string(),
})
export type StagingExternalUrl = z.infer<typeof stagingExternalUrlSchema>

/**
 * Nested page metadata from Spring Boot 3.x Page response.
 */
const pageMetadataSchema = z.object({
  size: z.coerce.number().default(0),
  number: z.coerce.number().default(0),
  totalElements: z.coerce.number().default(0),
  totalPages: z.coerce.number().default(1),
})

/**
 * Paginated response from backend (Spring Boot 3.x Page format).
 * Spring Boot 3.x nests pagination in a "page" object.
 */
export const paginatedUrlsSchema = z
  .object({
    content: z.array(stagingExternalUrlSchema),
    page: pageMetadataSchema.optional(),
  })
  .passthrough()
  .transform(data => ({
    content: data.content,
    totalElements: data.page?.totalElements ?? 0,
    totalPages: data.page?.totalPages ?? 1,
    size: data.page?.size ?? 0,
    number: data.page?.number ?? 0,
    first: (data.page?.number ?? 0) === 0,
    last: (data.page?.number ?? 0) >= (data.page?.totalPages ?? 1) - 1,
    empty: data.content.length === 0,
  }))
export type PaginatedUrls = z.infer<typeof paginatedUrlsSchema>

/**
 * Assignment result from backend.
 */
export const assignmentResultSchema = z.object({
  url: stagingExternalUrlSchema,
  adSpendRecordsReattributed: z.number(),
})
export type AssignmentResult = z.infer<typeof assignmentResultSchema>

/**
 * Bulk assignment result from backend.
 */
export const bulkAssignmentResultSchema = z.object({
  urlsAssigned: z.number(),
  adSpendRecordsReattributed: z.number(),
})
export type BulkAssignmentResult = z.infer<typeof bulkAssignmentResultSchema>

/**
 * General variant info from backend.
 */
export const generalVariantSchema = z.object({
  id: z.number(),
  variantSkuKey: z.string(),
  name: z.string(),
})
export type GeneralVariant = z.infer<typeof generalVariantSchema>

/**
 * AI matching result schema from backend.
 */
export const aiMatchingResultSchema = z.object({
  urlsProcessed: z.number(),
  suggestionsApplied: z.number(),
  invalidMatches: z.number(),
  message: z.string(),
})
export type AiMatchingResult = z.infer<typeof aiMatchingResultSchema>

// ============================================================================
// Query Keys
// ============================================================================

export const STAGING_URLS_QUERY_KEY = ['staging-external-urls'] as const
export const STAGING_URLS_COUNT_QUERY_KEY = ['staging-external-urls', 'count'] as const
export const GENERAL_VARIANT_QUERY_KEY = ['staging-external-urls', 'general-variant'] as const

// ============================================================================
// Service
// ============================================================================

export interface ListUrlsParams {
  resolved?: boolean
  source?: string
  variantId?: number
  page?: number
  size?: number
}

export const stagingExternalUrlsService = {
  /**
   * Fetch paginated URLs.
   * @param params Filter and pagination params
   * @returns Paginated URL list
   */
  async listUrls(params: ListUrlsParams = {}): Promise<PaginatedUrls> {
    const searchParams = new URLSearchParams()
    if (params.resolved !== undefined) {
      searchParams.set('resolved', String(params.resolved))
    }
    if (params.source) {
      searchParams.set('source', params.source)
    }
    if (params.variantId !== undefined) {
      searchParams.set('variantId', String(params.variantId))
    }
    if (params.page !== undefined) {
      searchParams.set('page', String(params.page))
    }
    if (params.size !== undefined) {
      searchParams.set('size', String(params.size))
    }

    const url = `/staging-external-urls${searchParams.toString() ? `?${searchParams.toString()}` : ''}`
    const response = await apiClient.get(url)
    return paginatedUrlsSchema.parse(response.data)
  },

  /**
   * Get count of unresolved URLs.
   */
  async countUnresolved(): Promise<number> {
    const response = await apiClient.get('/staging-external-urls/count')
    const data = z.object({ count: z.number() }).parse(response.data)
    return data.count
  },

  /**
   * Get the general (site-wide) variant for non-product-specific URLs.
   */
  async getGeneralVariant(): Promise<GeneralVariant> {
    const response = await apiClient.get('/staging-external-urls/general-variant')
    return generalVariantSchema.parse(response.data)
  },

  /**
   * Assign a URL to a variant.
   * @param uuid URL UUID
   * @param variantId Target variant ID
   */
  async assignToVariant(uuid: string, variantId: number): Promise<AssignmentResult> {
    const response = await apiClient.post(`/staging-external-urls/${uuid}/assign`, {
      variantId,
    })
    return assignmentResultSchema.parse(response.data)
  },

  /**
   * Unassign a URL from its variant (moves ad spend to __unmapped__).
   * @param uuid URL UUID
   */
  async unassignFromVariant(uuid: string): Promise<{ adSpendRecordsReattributed: number }> {
    const response = await apiClient.delete(`/staging-external-urls/${uuid}/assign`)
    return z.object({ adSpendRecordsReattributed: z.number() }).parse(response.data)
  },

  /**
   * Assign a URL to a tag (collection/category attribution).
   * @param uuid URL UUID
   * @param tagUuid Target tag UUID
   * @returns The updated URL (now tag-assigned and resolved)
   */
  async assignToTag(uuid: string, tagUuid: string): Promise<StagingExternalUrl> {
    const response = await apiClient.post(`/staging-external-urls/${uuid}/assign-tag`, {
      tagUuid,
    })
    return stagingExternalUrlSchema.parse(response.data)
  },

  /**
   * Unassign a URL from its tag (resurfaces it into the inbox).
   * @param uuid URL UUID
   */
  async unassignTag(uuid: string): Promise<void> {
    await apiClient.delete(`/staging-external-urls/${uuid}/assign-tag`)
  },

  /**
   * Delete a URL mapping permanently.
   * @param uuid URL UUID
   */
  async deleteUrl(uuid: string): Promise<void> {
    await apiClient.delete(`/staging-external-urls/${uuid}`)
  },

  /**
   * Bulk assign multiple URLs to the same variant.
   * @param urlUuids Array of URL UUIDs
   * @param variantId Target variant ID
   */
  async bulkAssign(urlUuids: string[], variantId: number): Promise<BulkAssignmentResult> {
    const response = await apiClient.post('/staging-external-urls/bulk-assign', {
      urlUuids,
      variantId,
    })
    return bulkAssignmentResultSchema.parse(response.data)
  },

  /**
   * Generate AI suggestions for unresolved URLs.
   * Requires OpenRouter API key to be configured on the backend.
   */
  async generateAiSuggestions(): Promise<AiMatchingResult> {
    const response = await apiClient.post('/staging-external-urls/ai-suggestions/generate')
    return aiMatchingResultSchema.parse(response.data)
  },

  /**
   * Clear AI suggestion for a URL.
   * Allows it to be re-suggested on the next AI run.
   */
  async clearSuggestion(uuid: string): Promise<void> {
    await apiClient.delete(`/staging-external-urls/${uuid}/suggestion`)
  },
}

// ============================================================================
// Hooks
// ============================================================================

/**
 * Fetches paginated unresolved URLs (inbox).
 *
 * @param params Query parameters
 * @param options Query options
 * @returns TanStack Query result
 */
export function useStagingExternalUrlsQuery(
  params: ListUrlsParams = { resolved: false },
  options?: { enabled?: boolean }
): UseQueryResult<PaginatedUrls, Error> {
  return useQuery<PaginatedUrls, Error>({
    queryKey: [...STAGING_URLS_QUERY_KEY, params],
    queryFn: () => stagingExternalUrlsService.listUrls(params),
    staleTime: 5 * 60 * 1000,
    enabled: options?.enabled ?? true,
  })
}

/**
 * Fetches count of unresolved URLs.
 *
 * @param options Query options
 * @returns TanStack Query result with count
 */
export function useUnresolvedUrlsCountQuery(
  options?: { enabled?: boolean }
): UseQueryResult<number, Error> {
  return useQuery<number, Error>({
    queryKey: STAGING_URLS_COUNT_QUERY_KEY,
    queryFn: () => stagingExternalUrlsService.countUnresolved(),
    staleTime: 5 * 60 * 1000,
    enabled: options?.enabled ?? true,
  })
}

/**
 * Fetches the general (site-wide) variant.
 *
 * @param options Query options
 * @returns TanStack Query result with general variant
 */
export function useGeneralVariantQuery(
  options?: { enabled?: boolean }
): UseQueryResult<GeneralVariant, Error> {
  return useQuery<GeneralVariant, Error>({
    queryKey: GENERAL_VARIANT_QUERY_KEY,
    queryFn: () => stagingExternalUrlsService.getGeneralVariant(),
    staleTime: 30 * 60 * 1000, // 30 minutes - rarely changes
    enabled: options?.enabled ?? true,
  })
}

/**
 * Mutation to assign a URL to a variant.
 */
export function useAssignUrlToVariantMutation(): UseMutationResult<
  AssignmentResult,
  Error,
  { uuid: string; variantId: number }
> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ uuid, variantId }) =>
      stagingExternalUrlsService.assignToVariant(uuid, variantId),
    onSuccess: async () => {
      // Invalidate URL queries to refresh the inbox
      await queryClient.invalidateQueries({ queryKey: STAGING_URLS_QUERY_KEY })
      await queryClient.invalidateQueries({ queryKey: STAGING_URLS_COUNT_QUERY_KEY })
    },
  })
}

/**
 * Mutation to unassign a URL from its variant.
 */
export function useUnassignUrlMutation(): UseMutationResult<
  { adSpendRecordsReattributed: number },
  Error,
  { uuid: string }
> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ uuid }) => stagingExternalUrlsService.unassignFromVariant(uuid),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: STAGING_URLS_QUERY_KEY })
      await queryClient.invalidateQueries({ queryKey: STAGING_URLS_COUNT_QUERY_KEY })
    },
  })
}

/**
 * Mutation to assign a URL to a tag (collection/category attribution).
 */
export function useAssignUrlToTagMutation(): UseMutationResult<
  StagingExternalUrl,
  Error,
  { uuid: string; tagUuid: string }
> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ uuid, tagUuid }) => stagingExternalUrlsService.assignToTag(uuid, tagUuid),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: STAGING_URLS_QUERY_KEY })
      await queryClient.invalidateQueries({ queryKey: STAGING_URLS_COUNT_QUERY_KEY })
    },
  })
}

/**
 * Mutation to unassign a URL from its tag.
 */
export function useUnassignTagMutation(): UseMutationResult<void, Error, { uuid: string }> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ uuid }) => stagingExternalUrlsService.unassignTag(uuid),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: STAGING_URLS_QUERY_KEY })
      await queryClient.invalidateQueries({ queryKey: STAGING_URLS_COUNT_QUERY_KEY })
    },
  })
}

/**
 * Mutation to delete a URL mapping.
 */
export function useDeleteUrlMutation(): UseMutationResult<void, Error, { uuid: string }> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ uuid }) => stagingExternalUrlsService.deleteUrl(uuid),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: STAGING_URLS_QUERY_KEY })
      void queryClient.invalidateQueries({ queryKey: STAGING_URLS_COUNT_QUERY_KEY })
    },
  })
}

/**
 * Mutation for bulk assignment.
 */
export function useBulkAssignUrlsMutation(): UseMutationResult<
  BulkAssignmentResult,
  Error,
  { urlUuids: string[]; variantId: number }
> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ urlUuids, variantId }) =>
      stagingExternalUrlsService.bulkAssign(urlUuids, variantId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: STAGING_URLS_QUERY_KEY })
      void queryClient.invalidateQueries({ queryKey: STAGING_URLS_COUNT_QUERY_KEY })
    },
  })
}

/**
 * Mutation to generate AI suggestions for unresolved URLs.
 * Triggers AI matching and refreshes URL list on success.
 */
export function useGenerateAiSuggestionsMutation(): UseMutationResult<
  AiMatchingResult,
  Error,
  void
> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: () => stagingExternalUrlsService.generateAiSuggestions(),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: STAGING_URLS_QUERY_KEY })
      void queryClient.invalidateQueries({ queryKey: STAGING_URLS_COUNT_QUERY_KEY })
    },
  })
}

/**
 * Mutation to clear AI suggestion for a URL.
 * Allows the URL to be re-suggested on the next AI generation run.
 */
export function useClearSuggestionMutation(): UseMutationResult<
  void,
  Error,
  { uuid: string }
> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ uuid }) => stagingExternalUrlsService.clearSuggestion(uuid),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: STAGING_URLS_QUERY_KEY })
    },
  })
}

export default stagingExternalUrlsService
